/**
 * Collapses the footer's content columns on small screens.
 *
 * Four stacked columns make a very tall footer on a phone — around 1,000px here, which is
 * more than a screen of scrolling past the actual content. Each column's label becomes a
 * disclosure and its contents collapse behind it.
 *
 * The newsletter column is deliberately left open: it is the one thing in the footer worth
 * acting on, and hiding it behind a tap would defeat the point.
 *
 * Progressive enhancement — with JS off, every column stays expanded and readable.
 */

const BREAKPOINT = '(max-width: 749px)';
const LABEL = '.text-block, .menu__heading, h2, h3, h4, h5, h6';

function collapse(column, index, total) {
  if (column.dataset.footerAccordion) return;

  // The menu block ships its own disclosure, so wrapping it would nest two.
  if (column.querySelector('details')) {
    column.dataset.footerAccordion = 'native';
    return;
  }

  const label = column.querySelector(LABEL);
  if (!label) return;

  // Leave the last column — the newsletter — open.
  if (index === total - 1) {
    column.dataset.footerAccordion = 'open';
    return;
  }

  // The label may sit inside a wrapper, so collect everything that is not its ancestor.
  const labelBranch = [...column.children].find((child) => child === label || child.contains(label));
  const rest = [...column.children].filter((child) => child !== labelBranch);
  if (!rest.length) return;

  column.dataset.footerAccordion = 'collapsed';

  const details = document.createElement('details');
  details.className = 'footer-accordion';

  const summary = document.createElement('summary');
  summary.className = 'footer-accordion__summary';
  summary.append(labelBranch);

  const body = document.createElement('div');
  body.className = 'footer-accordion__body';
  rest.forEach((child) => body.append(child));

  details.append(summary, body);
  column.append(details);
}

function restore(column) {
  const details = column.querySelector(':scope > .footer-accordion');
  if (!details) return;

  const summary = details.querySelector('.footer-accordion__summary');
  const body = details.querySelector('.footer-accordion__body');
  [...(summary?.children ?? [])].forEach((child) => column.append(child));
  [...(body?.children ?? [])].forEach((child) => column.append(child));
  details.remove();
  delete column.dataset.footerAccordion;
}

function apply() {
  const footer = document.querySelector('.shopify-section-group-footer-group [id*="__footer"]')
    ?? document.querySelector('.shopify-section-group-footer-group');
  if (!footer) return;

  // Group blocks nest: a row wraps the columns, and a column can wrap more blocks. Both
  // match the same selector, so pick by depth — the level holding the most siblings is
  // the row of columns. Selecting the outermost grabbed the row itself; selecting all of
  // them nested accordions inside accordions.
  const candidates = [...footer.querySelectorAll('.group-block-content')]
    .filter((el) => el.querySelector(LABEL));

  const byDepth = new Map();
  for (const el of candidates) {
    let depth = 0;
    for (let n = el.parentElement; n; n = n.parentElement) {
      if (n.classList?.contains('group-block-content')) depth += 1;
    }
    byDepth.set(depth, [...(byDepth.get(depth) ?? []), el]);
  }

  let columns = [];
  for (const group of byDepth.values()) {
    if (group.length > columns.length) columns = group;
  }
  if (columns.length < 2) return;

  const small = window.matchMedia(BREAKPOINT).matches;
  columns.forEach((column, index) => {
    if (small) collapse(column, index, columns.length);
    else restore(column);
  });
}

function init() {
  apply();
  window.matchMedia(BREAKPOINT).addEventListener('change', apply);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
